// Rider signals: what a game receives instead of the raw Bluetooth data.
// Computed once for all creations, in O(1) per sample: smoothed power, fraction of FTP, zone,
// energy, anaerobic reserve (W′bal, Skiba differential model) and sprint detection.
import { zoneOf } from './workouts.js';

export interface Signals {
  /** Elapsed time (s). */
  t: number;
  power: number; cadence: number; hr: number;
  /** Power smoothed over ~3 s (W). */
  power3s: number;
  /** Smoothed power relative to FTP (1 = threshold). */
  ftpFrac: number;
  /** Power zone 1 to 7. */
  zone: number;
  /** Energy produced (kJ). */
  kj: number;
  /** Remaining anaerobic reserve, from 0 (empty) to 1 (full). */
  wbal: number;
  /** True on the sample where a sprint starts. */
  sprint: boolean;
  /** True while the sprint lasts. */
  sprinting: boolean;
  /** Peak power of the current sprint or of the last sprint (W). */
  sprintPeak: number;
}

/**
 * One step of the Skiba differential model (Clarke and Skiba): above critical power cp the reserve w (J)
 * drains by the same amount; below it, it recharges, faster the emptier it is and the lower you ride.
 */
export function wbalStep(w: number, power: number, cp: number, wprime: number, dt: number): number {
  const next = power > cp ? w - (power - cp) * dt : w + ((wprime - w) * (cp - power) * dt) / wprime;
  return Math.min(wprime, Math.max(0, next));
}

export interface SignalOptions { ftp: number; wprime?: number }

export class SignalTracker {
  private s: Signals = { t: 0, power: 0, cadence: 0, hr: 0, power3s: 0, ftpFrac: 0, zone: 1, kj: 0, wbal: 1, sprint: false, sprinting: false, sprintPeak: 0 };
  private w: number;
  private calm = 99;
  readonly ftp: number; readonly wprime: number;
  constructor(o: SignalOptions) { this.ftp = Math.max(50, o.ftp); this.wprime = o.wprime ?? 20000; this.w = this.wprime; }

  get current(): Readonly<Signals> { return this.s; }

  push(power: number, cadence: number, hr: number, dt: number): Readonly<Signals> {
    const s = this.s, p = Math.max(0, Number.isFinite(power) ? power : 0);
    if (dt <= 0) return s;
    s.t += dt; s.power = p; s.cadence = Math.max(0, cadence || 0); s.hr = Math.max(0, hr || 0);
    s.power3s += (p - s.power3s) * (1 - Math.exp(-dt / 1.5));
    s.ftpFrac = s.power3s / this.ftp;
    s.zone = zoneOf(s.ftpFrac).id;
    s.kj += (p * dt) / 1000;
    this.w = wbalStep(this.w, p, this.ftp, this.wprime, dt);
    s.wbal = this.w / this.wprime;
    // sprint: peak > 1.6 FTP after at least 3 s under 1.2 FTP; ends when power drops back
    s.sprint = false;
    if (!s.sprinting && p > this.ftp * 1.6 && this.calm >= 3) { s.sprinting = true; s.sprint = true; s.sprintPeak = p; }
    else if (s.sprinting) { s.sprintPeak = Math.max(s.sprintPeak, p); if (p < this.ftp * 1.1) s.sprinting = false; }
    this.calm = p < this.ftp * 1.2 ? this.calm + dt : 0;
    return s;
  }
}
